import { apiRequest, storedToken } from "./api";
import { getDeploymentConfig } from "./deployment-config";
import { fetchWithLogging } from "./fetch-logger";

// Settings that follow the account to every browser and device, kept by
// play-api in DynamoDB (GET/PUT /preferences) as the localStorage entries
// they already are. The browser's copy stays the one the app reads, so
// playing signed out works as before. At load (main.tsx, before render) a
// signed-in browser takes the account's copy, unless it changed something
// that never reached the cloud, which goes up instead; each change after
// that is sent a second later. Device things (the guide's width, whether
// it is open) stay in the browser.

const KEYS = [
  "pocket-play.muted",
  "pocket-play.fastForward",
  "pocket-play.autoSave",
  "pocket-play.movement",
  "pocket-play.shoulders",
  "pocket-play.keymap",
  "pocket-play.librarySort",
];
const PREFIXES = ["pocket-play.cheats.", "pocket-play.n64Move."];
/** Set while a change hasn't reached the cloud. */
const DIRTY_KEY = "pocket-play.prefsDirty";
const PUSH_DELAY_MS = 1000;
const LOAD_TIMEOUT_MS = 2500;

export const followsAccount = (key: string) => KEYS.includes(key) || PREFIXES.some((p) => key.startsWith(p));

/**
 * Runs `listener` after the account's settings replace the browser's (for
 * modules that cache one). A window event, not a set of listeners here:
 * keymap.ts registers while this module is still loading (it imports this
 * module through the API and auth modules), and only a function declaration
 * is ready that early.
 */
export function onPreferencesApplied(listener: () => void): () => void {
  window.addEventListener("pocket-play:preferences-applied", listener);
  return () => window.removeEventListener("pocket-play:preferences-applied", listener);
}

/** Writes a setting (null removes it) and, for one that follows the account, sends it up. */
export function setPref(key: string, value: string | null) {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
    if (!followsAccount(key)) return;
    localStorage.setItem(DIRTY_KEY, "1");
  } catch {
    return;
  }
  schedulePush();
}

function collect(): Record<string, string> {
  const prefs: Record<string, string> = {};
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && followsAccount(key)) prefs[key] = localStorage.getItem(key) ?? "";
  }
  return prefs;
}

let timer: number | undefined;
function schedulePush() {
  window.clearTimeout(timer);
  timer = window.setTimeout(() => void push(), PUSH_DELAY_MS);
}

async function push() {
  if (!storedToken()) return;
  try {
    await apiRequest("/preferences", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ prefs: collect() }),
    });
    localStorage.removeItem(DIRTY_KEY);
  } catch {
    // Still marked; the next change or load sends it.
  }
}

// At load the tokens may still be in the URL hash (sign-in just returned),
// so these calls carry the token they're given rather than apiRequest's.
// The limit covers reading the answer too, not just its arrival.
function call(token: string, init: RequestInit = {}): Promise<Response> {
  return fetchWithLogging(
    `${getDeploymentConfig().apiUrl}/preferences`,
    { ...init, headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", ...init.headers } },
    { timeoutMs: LOAD_TIMEOUT_MS },
  );
}

function tokenAtLoad(): string | null {
  const hash = new URLSearchParams(window.location.hash.slice(1));
  return hash.get("id_token") || hash.get("access_token") || storedToken();
}

/**
 * Makes the browser's settings the signed-in account's. Never throws and
 * gives up after a couple of seconds: offline, the browser's copy is used.
 */
export async function settlePreferences(): Promise<void> {
  const token = tokenAtLoad();
  if (!token) return;
  try {
    const dirty = localStorage.getItem(DIRTY_KEY) === "1";
    const cloud = dirty ? null : await call(token).then((r) => (r.ok ? r.json() : null));
    if (dirty || (cloud && !cloud.updatedAt)) {
      // Changed here and not sent yet, or the account has none: this browser's go up.
      const response = await call(token, { method: "PUT", body: JSON.stringify({ prefs: collect() }) });
      if (response.ok) localStorage.removeItem(DIRTY_KEY);
      return;
    }
    if (!cloud) return;
    const prefs = cloud.prefs as Record<string, string>;
    for (const key of Object.keys(collect())) if (!(key in prefs)) localStorage.removeItem(key);
    for (const [key, value] of Object.entries(prefs)) if (followsAccount(key)) localStorage.setItem(key, value);
    window.dispatchEvent(new Event("pocket-play:preferences-applied"));
  } catch {
    // The browser's copy stands.
  }
}
